import { motion, AnimatePresence } from 'framer-motion';
import { Hospital, Plus, Eye, UserPlus, Trash2, Search, User, Mail, CreditCard, Calendar, Package as PackageIcon, X, Building2, List, Coins } from 'lucide-react';
import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import DataTable from 'react-data-table-component';
import { BASE_URL } from '../config';
import { notify } from '../utils/toast';

export default function HospitalsDashboard({ onNavigate }) {
  const [stats, setStats] = useState({
    total_hospitals: 0,
    total_doctors: 0
  });
  const [hospitals, setHospitals] = useState([]);
  const [filteredHospitals, setFilteredHospitals] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [viewingHospital, setViewingHospital] = useState(null);
  const [showPackageModal, setShowPackageModal] = useState(false);
  const [selectedHospital, setSelectedHospital] = useState(null);
  const [packages, setPackages] = useState([]);
  const [selectedPackage, setSelectedPackage] = useState('');
  const [showAddonModal, setShowAddonModal] = useState(false);
  const [addonCredits, setAddonCredits] = useState('');

  useEffect(() => {
    fetchStats();
    fetchHospitals();
    fetchPackages();
  }, []);

  useEffect(() => {
    setFilteredHospitals(hospitals);
  }, [hospitals]);

  const handleSearchChange = (e) => {
    const term = e.target.value.toLowerCase();
    setSearchTerm(term);
    const filtered = hospitals.filter(h =>
      h.username?.toLowerCase().includes(term) ||
      h.email?.toLowerCase().includes(term) ||
      h.package_name?.toLowerCase().includes(term)
    );
    setFilteredHospitals(filtered);
  };

  // DataTable columns configuration
  const columns = useMemo(() => [
    {
      name: 'S.No',
      selector: (row, index) => index + 1,
      sortable: false,
      width: '80px',
      center: true,
    },
    {
      name: 'Username',
      selector: row => row.username,
      sortable: true,
      grow: 1,
    },
    {
      name: 'Email',
      selector: row => row.email,
      sortable: true,
      grow: 2,
      cell: row => (
        <div className="flex items-center gap-2 text-slate-600">
          <Mail className="w-4 h-4" />
          {row.email}
        </div>
      ),
    },
    {
      name: 'Package',
      selector: row => row.package_name,
      sortable: true,
      cell: row => (
        <span className="inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-medium bg-primary-100 text-primary-700">
          <PackageIcon className="w-4 h-4 mr-1.5" />
          {row.package_name}
        </span>
      ),
    },
    {
      name: 'Credits',
      selector: row => row.credit_limit,
      sortable: true,
      width: '100px',
      cell: row => (
        <div className="flex items-center gap-1.5 text-slate-700">
          <CreditCard className="w-4 h-4 text-slate-600" />
          <span className="font-medium">{row.credit_limit}</span>
        </div>
      ),
    },
    {
      name: 'Days',
      selector: row => row.days_limit,
      sortable: true,
      width: '100px',
      cell: row => (
        <div className="flex items-center gap-1.5 text-slate-700">
          <Calendar className="w-4 h-4 text-slate-600" />
          <span className="font-medium">{row.days_limit}</span>
        </div>
      ),
    },
    {
      name: 'Actions',
      center: true,
      width: '300px',
      cell: row => (
        <div className="flex items-center justify-center gap-1.5">
          <button
            onClick={() => handleViewMore(row)}
            title="View Details"
            className="p-2 text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
          >
            <Eye className="w-5 h-5" />
          </button>
          <button
            onClick={() => handleAddDoctors(row)}
            title="Manage Doctors"
            className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
          >
            <UserPlus className="w-5 h-5" />
          </button>
          <button
            onClick={() => handleChangePackage(row)}
            title="Update Package"
            className="p-2 text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
          >
            <PackageIcon className="w-5 h-5" />
          </button>
          <button
            onClick={() => handleAddOn(row)}
            title="Add-On Credits"
            className="p-2 text-amber-600 hover:bg-amber-50 rounded-lg transition-colors"
          >
            <Coins className="w-5 h-5" />
          </button>
          <button
            onClick={() => handleDelete(row.user_id)}
            title="Delete Hospital"
            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
          >
            <Trash2 className="w-5 h-5" />
          </button>
        </div>
      ),
    },
  ], []);

  // Custom styles for DataTable
  const customStyles = {
    headRow: {
      style: {
        background: 'linear-gradient(to right, rgb(59 130 246), rgb(37 99 235))',
        color: 'white',
        fontSize: '14px',
        fontWeight: '600',
        minHeight: '56px',
      },
    },
    headCells: {
      style: {
        paddingLeft: '24px',
        paddingRight: '24px',
        color: 'white',
      },
    },
    rows: {
      style: {
        minHeight: '64px',
        '&:hover': {
          backgroundColor: '#f8fafc',
        },
      },
    },
    cells: {
      style: {
        paddingLeft: '24px',
        paddingRight: '24px',
      },
    },
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('accessToken');
      const response = await axios.get(`${BASE_URL}/api/hospitals/stats/?type=hospital`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setStats(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching hospital stats:', error);
      setLoading(false);
    }
  };

  const fetchHospitals = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.get(`${BASE_URL}/api/hospitals/list-with-details/`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHospitals(response.data);
    } catch (error) {
      console.error('Error fetching hospitals:', error);
    }
  };

  const fetchPackages = async () => {
    try {
      const response = await axios.get(`${BASE_URL}/api/packages/all/`);
      setPackages(response.data);
    } catch (error) {
      console.error('Error fetching packages:', error);
    }
  };

  const handleViewMore = (hospital) => {
    setViewingHospital(hospital);
  };

  const handleAddDoctors = (hospital) => {
    // Store hospital info in localStorage for the add doctors page
    localStorage.setItem('selectedHospitalId', hospital.user_id);
    localStorage.setItem('selectedHospitalName', hospital.username);
    // Navigate to add doctors page
    onNavigate('hospitals_dashboard/add_doctors');
  };

  const handleChangePackage = (hospital) => {
    setSelectedHospital(hospital);
    setSelectedPackage(hospital.package_id || '');
    setShowPackageModal(true);
  };

  const handleAddOn = (hospital) => {
    setSelectedHospital(hospital);
    setAddonCredits('');
    setShowAddonModal(true);
  };

  const handleAddonAssign = async () => {
    if (!addonCredits || parseFloat(addonCredits) <= 0) {
      notify.error('Please enter valid credits');
      return;
    }

    try {
      const token = localStorage.getItem('accessToken');
      await axios.post(
        `${BASE_URL}/api/superadmin/assign-addon/${selectedHospital.user_id}/`,
        { credits: addonCredits },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      notify.success(`${addonCredits} credits added successfully`);
      setShowAddonModal(false);
      setAddonCredits('');
    } catch (error) {
      console.error('Error assigning add-on credits:', error);
      notify.error(error.response?.data?.error || 'Error assigning credits');
    }
  };

  const handlePackageUpdate = async () => {
    if (!selectedPackage) {
      notify.error('Please select a package');
      return;
    }

    try {
      const token = localStorage.getItem('accessToken');
      await axios.put(
        `${BASE_URL}/api/superadmin/${selectedHospital.user_id}/package/`,
        { package_id: selectedPackage },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      notify.success('Package updated successfully');
      setShowPackageModal(false);
      fetchHospitals();
    } catch (error) {
      console.error('Error updating package:', error);
      notify.error('Error updating package');
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this hospital?')) return;

    try {
      const token = localStorage.getItem('accessToken');
      await axios.delete(`${BASE_URL}/api/hospitals/superadmin/`, {
        headers: { Authorization: `Bearer ${token}` },
        data: { user_id: userId },
      });
      notify.success('Hospital deleted successfully');
      fetchHospitals();
    } catch (error) {
      console.error('Error deleting hospital:', error);
      notify.error('Error deleting hospital');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header with Register Button */}
      <div className="flex items-center justify-between border-b border-slate-200 pb-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Hospital Management</h1>
        </div>
        <button
          onClick={() => onNavigate('super_hospital')}
          className="flex items-center gap-2 px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white font-medium rounded transition-colors text-sm"
        >
          <Plus className="w-4 h-4" />
          Register Hospital
        </button>
      </div>

      {/* <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-gradient-to-r from-red-50 to-orange-50 border border-red-200 rounded-lg p-5"
      >
        <div className="flex items-start gap-3">
          <div className="w-12 h-12 bg-red-500 rounded-lg flex items-center justify-center flex-shrink-0">
            <Hospital className="w-6 h-6 text-slate-900" />
          </div> 
          <div>
            <h4 className="font-semibold text-red-900 mb-1">Hospital Operations</h4>
            <p className="text-sm text-red-700">
              Manage hospital registrations, view hospital details, and oversee all hospital-related operations from this dashboard.
            </p>
          </div>
        </div>
      </motion.div> */}

      {/* Stats and Search Row */}
      <div className="flex items-center justify-between gap-4">
        {/* Stats Cards - Compact */}
        <div className="flex gap-3">
          <div className="bg-white rounded border border-slate-200 p-3 shadow-sm">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 bg-red-500/20 rounded flex items-center justify-center">
                <Hospital className="w-4 h-4 text-red-400" />
              </div>
              <h4 className="text-slate-600 text-xs font-medium">Total Hospitals</h4>
            </div>
            <p className="text-2xl font-bold text-slate-900">
              {loading ? '...' : stats.total_hospitals}
            </p>
          </div>

          <div className="bg-white rounded border border-slate-200 p-3 shadow-sm">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 bg-primary-500/20 rounded flex items-center justify-center">
                <List className="w-4 h-4 text-primary-400" />
              </div>
              <h4 className="text-slate-600 text-xs font-medium">Total Doctors</h4>
            </div>
            <p className="text-2xl font-bold text-slate-900">
              {loading ? '...' : stats.total_doctors}
            </p>
          </div>
        </div>

        {/* Search Bar - Right Side */}
        <div className="w-80">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-600" />
            <input
              type="text"
              placeholder="Search hospitals..."
              value={searchTerm}
              onChange={handleSearchChange}
              className="w-full pl-9 pr-3 py-2 bg-primary-50 border-2 border-primary-300 rounded-lg text-slate-900 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm shadow-sm"
            />
          </div>
        </div>
      </div>

          {/* Hospitals DataTable */}
          <div className="bg-white rounded border border-slate-200 overflow-hidden shadow-sm">
            <DataTable
              columns={columns}
              data={filteredHospitals}
              pagination
              paginationPerPage={10}
              paginationRowsPerPageOptions={[10, 25, 50, 100]}
              customStyles={customStyles}
              highlightOnHover
              pointerOnHover
              responsive
              noDataComponent={
                <div className="text-center py-12">
                  <Building2 className="w-16 h-16 text-slate-400 mx-auto mb-4" />
                  <p className="text-slate-600 text-lg">No hospitals found</p>
                </div>
              }
            />
          </div>

      {/* View Details Modal */}
      <AnimatePresence>
        {viewingHospital && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
            onClick={() => setViewingHospital(null)}
          >
            <motion.div
              initial={{ scale: 0.95 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.95 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded border border-slate-200 max-w-2xl w-full p-6 shadow-xl"
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold text-slate-900">Hospital Details</h3>
                <button onClick={() => setViewingHospital(null)} className="p-2 hover:bg-slate-750 rounded-lg">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="space-y-3">
                <div className="flex items-center justify-between py-2 border-b border-slate-200">
                  <span className="text-slate-600">Username:</span>
                  <span className="font-medium text-slate-900">{viewingHospital.username}</span>
                </div>
                <div className="flex items-center justify-between py-2 border-b border-slate-200">
                  <span className="text-slate-600">Email:</span>
                  <span className="font-medium text-slate-900">{viewingHospital.email}</span>
                </div>
                <div className="flex items-center justify-between py-2 border-b border-slate-200">
                  <span className="text-slate-600">Name:</span>
                  <span className="font-medium text-slate-900">{viewingHospital.name || 'N/A'}</span>
                </div>
                <div className="flex items-center justify-between py-2 border-b border-slate-200">
                  <span className="text-slate-600">Address:</span>
                  <span className="font-medium text-slate-900">{viewingHospital.address || 'N/A'}</span>
                </div>
                <div className="flex items-center justify-between py-2 border-b border-slate-200">
                  <span className="text-slate-600">Contact:</span>
                  <span className="font-medium text-slate-900">{viewingHospital.contact_number || 'N/A'}</span>
                </div>
                <div className="flex items-center justify-between py-2 border-b border-slate-200">
                  <span className="text-slate-600">Package:</span>
                  <span className="px-3 py-1 bg-primary-100 text-primary-700 rounded-full text-sm font-medium">{viewingHospital.package_name}</span>
                </div>
                <div className="flex items-center justify-between py-2 border-b border-slate-200">
                  <span className="text-slate-600">Credits:</span>
                  <span className="font-medium text-slate-900">{viewingHospital.credit_limit}</span>
                </div>
                <div className="flex items-center justify-between py-2">
                  <span className="text-slate-600">Days:</span>
                  <span className="font-medium text-slate-900">{viewingHospital.days_limit}</span>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Package Modal */}
      <AnimatePresence>
        {showPackageModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
            onClick={() => setShowPackageModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.95 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-lg shadow-xl max-w-md w-full p-6"
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold text-slate-900">Change Package</h3>
                <button onClick={() => setShowPackageModal(false)} className="p-2 hover:bg-slate-750 rounded-lg">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-slate-700 mb-2">Select Package</label>
                <select
                  value={selectedPackage}
                  onChange={(e) => setSelectedPackage(e.target.value)}
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none"
                >
                  <option value="">Choose a package...</option>
                  {packages.map((pkg) => (
                    <option key={pkg.package_ID} value={pkg.package_ID}>
                      {pkg.package_name} - ${pkg.price} ({pkg.credit_limit} credits, {pkg.days_limit} days)
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowPackageModal(false)}
                  className="flex-1 px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-100"
                >
                  Cancel
                </button>
                <button
                  onClick={handlePackageUpdate}
                  className="flex-1 px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600"
                >
                  Update
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Add-On Credits Modal */}
      <AnimatePresence>
        {showAddonModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
            onClick={() => setShowAddonModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.95 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-lg shadow-xl max-w-md w-full p-6"
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold text-slate-900 flex items-center gap-2">
                  <Coins className="w-6 h-6 text-amber-500" />
                  Assign Add-On Credits
                </h3>
                <button onClick={() => setShowAddonModal(false)} className="p-2 hover:bg-slate-750 rounded-lg">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="mb-4">
                <p className="text-sm text-slate-600 mb-4">
                  Assign additional credits to <span className="font-semibold">{selectedHospital?.username}</span>
                </p>
                <label className="block text-sm font-medium text-slate-700 mb-2">Credits Amount</label>
                <input
                  type="number"
                  step="0.01"
                  min="0.01"
                  value={addonCredits}
                  onChange={(e) => setAddonCredits(e.target.value)}
                  placeholder="Enter credits (e.g., 1000)"
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent outline-none"
                />
                <p className="text-xs text-slate-600 mt-2">
                  These credits will be added to the hospital's add-on balance
                </p>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowAddonModal(false)}
                  className="flex-1 px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-100"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddonAssign}
                  className="flex-1 px-4 py-2 bg-amber-500 text-slate-900 rounded-lg hover:bg-amber-600 flex items-center justify-center gap-2"
                >
                  <Coins className="w-4 h-4" />
                  Assign Credits
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
